import React, { useState } from "react";
import CustomButton from "../../components/form/CustomButton";

const formatScheduleValue = (date, time) => {
    if (!date && !time) return "—";
    return [date, time].filter(Boolean).join(" ");
};

const StopsList = ({
    items,
    deleteItem,
    setNewStop,
    onEditStop,
    onReorderStops,
}) => {
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDrop = (targetIndex) => {
        if (
            draggedIndex === null ||
            targetIndex === null ||
            draggedIndex === targetIndex
        ) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        onReorderStops?.(draggedIndex, targetIndex);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <>
            <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] text-cs-muted">
                    Drag and drop rows to reorder stops.
                </p>

                {setNewStop && (
                    <CustomButton
                        type="button"
                        onClick={() => setNewStop(true)}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              shadow-md hover:shadow-lg
              transition-all
            "
                    >
                        Add Stop
                    </CustomButton>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead className="bg-cs-bg-alt border-b border-cs-border">
                        <tr>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide">
                                #
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide">
                                Address
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide">
                                Status
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide">
                                Trailer
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide text-right">
                                Mile
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide">
                                Schedule
                            </th>
                            <th className="px-4 py-2 text-[11px] font-semibold text-cs-muted uppercase tracking-wide text-right">
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-cs-border">
                        {items.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-4 py-4 text-xs text-cs-muted text-center"
                                >
                                    No stops added yet.
                                </td>
                            </tr>
                        )}

                        {items.map((item, i) => {
                            const isDragging = draggedIndex === i;
                            const isDragTarget = dragOverIndex === i && draggedIndex !== i;

                            return (
                                <tr
                                    key={item.id || `${item.order}-${item.name}-${i}`}
                                    draggable={!!onReorderStops}
                                    onDragStart={() => setDraggedIndex(i)}
                                    onDragOver={(event) => {
                                        if (!onReorderStops) return;
                                        event.preventDefault();
                                        setDragOverIndex(i);
                                    }}
                                    onDragLeave={() => {
                                        if (dragOverIndex === i) {
                                            setDragOverIndex(null);
                                        }
                                    }}
                                    onDrop={() => handleDrop(i)}
                                    onDragEnd={() => {
                                        setDraggedIndex(null);
                                        setDragOverIndex(null);
                                    }}
                                    className={`transition-colors ${
                                        isDragging
                                            ? "opacity-60 bg-cs-primary-soft"
                                            : isDragTarget
                                                ? "bg-cs-bg-alt"
                                                : "hover:bg-cs-surface-alt"
                                    }`}
                                >
                                    <td className="px-4 py-2 text-xs text-cs-muted">
                                        <div className="inline-flex items-center gap-2 cursor-grab active:cursor-grabbing">
                                            <span className="material-symbols-outlined text-sm text-cs-muted">
                                                drag_indicator
                                            </span>
                                            <span>{item.order}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-cs-dark">
                                        {item.name}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-cs-muted">
                                        {item.status?.name}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-cs-muted">
                                        {item.trailer?.number || item.trailer?.name || item.effective_trailer?.number || item.effective_trailer?.name || item.trailer_id || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right text-cs-dark">
                                        {item.mile}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-cs-muted">
                                        <div className="space-y-1">
                                            <div>
                                                <span className="font-medium text-cs-dark">Start:</span>{" "}
                                                {formatScheduleValue(item.date, item.load_time)}
                                            </div>
                                            <div>
                                                <span className="font-medium text-cs-dark">End:</span>{" "}
                                                {formatScheduleValue(item.delivery_date, item.delivery_time)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <div className="inline-flex items-center justify-end gap-2">
                                            {onEditStop && (
                                                <button
                                                    type="button"
                                                    onClick={() => onEditStop(item)}
                                                    className="
                  inline-flex items-center justify-center
                  h-8 w-8
                  rounded-lg
                  border border-cs-border
                  bg-cs-bg
                  hover:bg-cs-bg-alt
                  transition-colors
                "
                                                >
                                                    <i className="fas fa-pen text-cs-primary text-xs" />
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                onClick={() => deleteItem("stops", item)}
                                                className="
                inline-flex items-center justify-center
                h-8 w-8
                rounded-lg
                border border-cs-border
                bg-cs-bg
                hover:bg-cs-bg-alt
                transition-colors
              "
                                            >
                                                <i className="fas fa-trash text-cs-danger text-xs" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

        </>
    );
};

export default StopsList;
